/**
 * Executive Dossier Page - Full-page view for executive intelligence dossier.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ExecutiveDossierView, ExecutiveDetail } from '../components/company/dossier';

export function ExecutiveDossierPage() {
  const { companyId, executiveId } = useParams<{ companyId: string; executiveId: string }>();
  const navigate = useNavigate();
  const [executive, setExecutive] = useState<ExecutiveDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!executiveId) {
      setError('No executive ID provided');
      setLoading(false);
      return;
    }

    const fetchExecutiveAndAssessment = async () => {
      setLoading(true);
      setError(null);

      try {
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

        // Fetch executive data
        const execResponse = await fetch(`${API_BASE_URL}/api/executives/${executiveId}`);
        if (!execResponse.ok) {
          throw new Error('Failed to fetch executive details');
        }
        const execData = await execResponse.json();

        // Fetch assessment data (don't fail if assessment not available)
        let assessment = null;
        try {
          const assessResponse = await fetch(`${API_BASE_URL}/api/executives/${executiveId}/assessment`);
          if (assessResponse.ok) {
            const assessData = await assessResponse.json();
            assessment = assessData.assessment;
          }
        } catch {
          // Assessment not available - this is expected for executives without assessments
        }

        // Merge assessment into executive data
        setExecutive({
          ...execData.executive,
          assessment,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchExecutiveAndAssessment();
  }, [executiveId]);

  const handleBack = () => {
    if (companyId) {
      navigate(`/companies/${companyId}?tab=leadership`);
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading executive dossier...</p>
        </div>
      </div>
    );
  }

  if (error || !executive) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="mt-4 text-lg font-semibold text-gray-900">Error Loading Dossier</h2>
          <p className="mt-2 text-gray-600">{error || 'Executive not found'}</p>
          <button
            onClick={handleBack}
            className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-500"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return <ExecutiveDossierView executive={executive} onBack={handleBack} />;
}

export default ExecutiveDossierPage;
